import { useCallback, useMemo } from "react";
import { useSession } from "next-auth/react";
import { useEffectEvent } from "@/hooks/useEffectEvent";
import {
  useGetCreditsQuery,
  useGetSubscriptionsQuery,
  useGetPermissionsQuery,
  useLazyGetCreditsQuery,
  useLazyGetSubscriptionsQuery,
  useLazyGetPermissionsQuery,
} from "@/redux/api/userApi";
import {
  GetSubscriptionsType,
  GetCreditsType,
  Permission,
  PermissionName,
  PermissionLevel,
  PaymentsSubscriptions,
} from "@/types/users";
import { generativeCreditApi } from "@/redux/api/credit/generativeCreditApi";

type UseSubscriptionParams = {
  refetchOnReconnect?: boolean;
  refetchOnFocus?: boolean;
  refetchOnMountOrArgChange?: boolean | number;
}

type UseSubscriptionType = {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => Promise<void>;
  fetch: () => Promise<Pick<
    UseSubscriptionType,
    "plan" | "apiPlan" | "permission" | "quota" | "credits" |
    "generativeCredits" | "subscriptionInfo" | "apiSubscriptionInfo"
  >>;
  plan: {
    name: "free" | "starter" | "pro" | "personal" | "team" | "enterprise";
    normalized: "free" | "personal" | "team" | "enterprise";
    raw: string;
    unlimited: boolean;
    interval: "month" | "year" | null;
    level: { current: number; max: number };
    next: "personal" | "team" | "enterprise" | null;
    seats: number | null;
    servicePeriodStart: Date | null;
    servicePeriodEnd: Date | null;
  } | null;
  apiPlan: {
    name: "free" | "standard" | "premium" | "enterprise";
    normalized: "free" | "standard" | "premium" | "enterprise";
    raw: string;
    level: { current: number; max: number };
    next: "standard" | "premium" | "enterprise" | null;
    servicePeriodStart: Date | null;
    servicePeriodEnd: Date | null;
  } | null;
  permission: { [K in PermissionName]: boolean } | null;
  quota: { [K in PermissionName]: number } | null;
  credits: GetCreditsType | null;
  generativeCredits: {
    remainingCredits: number;
    totalCredits: number;
    reservedCredits: number;
    creditDetail: {
      basicCredits: number;
      addOnCredits: number;
      extraCredits: number;
    };
  } | null;
  subscriptionInfo: PaymentsSubscriptions | null;
  apiSubscriptionInfo: PaymentsSubscriptions | null;
};

const maxPermissionLevel = Object.values(PermissionLevel)
  .filter((v) => !Number.isInteger(v))
  .length - 1;

export function useSubscription(options?: UseSubscriptionParams): UseSubscriptionType {
  const session = useSession();
  const isLoggedIn = !!session.data?.user?.id;

  const {
    data: credits = null,
    isLoading: isCreditsLoading,
    isFetching: isCreditsFetching,
    refetch: refetchCredits,
  } = useGetCreditsQuery(undefined, {
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
  });
  const {
    data: subscription = null,
    isLoading: isSubscriptionLoading,
    isFetching: isSubscriptionFetching,
    refetch: refetchSubscription,
  } = useGetSubscriptionsQuery(undefined, {
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
  });
  const {
    data: apiSubscription = null,
    isLoading: isApiSubscriptionLoading,
    isFetching: isApiSubscriptionFetching,
    refetch: refetchApiSubscription,
  } = useGetSubscriptionsQuery("api", {
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
  })
  const {
    data: permissions = null,
    isLoading: isPermissionsLoading,
    isFetching: isPermissionsFetching,
    refetch: refetchPermissions,
  } = useGetPermissionsQuery(undefined, {
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
  });
  const {
    data: reservedGenerativeCredits = null,
    isLoading: isReservedGenerativeCreditsLoading,
    isFetching: isReservedGenerativeCreditsFetching,
    refetch: refetchReservedGenerativeCredits,
  } = generativeCreditApi.useGetReservedGenerativeCreditsQuery(undefined, {
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? false,
    skip: !isLoggedIn,
  });

  const refetch = useCallback(async () => {
    await Promise.all([
      refetchCredits(),
      refetchSubscription(),
      refetchApiSubscription(),
      refetchPermissions(),
      refetchReservedGenerativeCredits(),
    ]);
  }, [
    refetchCredits,
    refetchSubscription,
    refetchPermissions,
    refetchReservedGenerativeCredits,
    refetchApiSubscription,
  ]);

  const [getCredits] = useLazyGetCreditsQuery();
  const [getSubscriptions] = useLazyGetSubscriptionsQuery();
  const [getApiSubscriptions] = useLazyGetSubscriptionsQuery();
  const [getPermissions] = useLazyGetPermissionsQuery();
  const [getReservedGenerativeCredits] =
    generativeCreditApi.useLazyGetReservedGenerativeCreditsQuery();

  const isLoading = useMemo(() => {
    return (
      isCreditsLoading ||
      isSubscriptionLoading ||
      isApiSubscriptionLoading ||
      isPermissionsLoading ||
      isReservedGenerativeCreditsLoading
    )
  }, [
    isCreditsLoading,
    isSubscriptionLoading,
    isApiSubscriptionLoading,
    isPermissionsLoading,
    isReservedGenerativeCreditsLoading,
  ]);

  const isFetching = useMemo(() => {
    return (
      isCreditsFetching ||
      isSubscriptionFetching ||
      isApiSubscriptionFetching ||
      isPermissionsFetching ||
      isReservedGenerativeCreditsFetching
    )
  }, [
    isCreditsFetching,
    isSubscriptionFetching,
    isApiSubscriptionFetching,
    isPermissionsFetching,
    isReservedGenerativeCreditsFetching,
  ]);

  const getNormalizedSubscription = useCallback((params: {
    subscription: GetSubscriptionsType | null;
    permissions: Permission[] | null;
  }): Pick<UseSubscriptionType, "plan" | "permission" | "quota"> => {
    const { subscription: s, permissions: p } = params;

    if (!s || !p) {
      return { plan: null, permission: null, quota: null };
    }

    const rawPlanName = s.plan === "none" ? "Free" : s.plan;
    const lowerCasePlanName = rawPlanName.toLowerCase();
    const cleanedPlanName = lowerCasePlanName
      .replace(/AI Studios/ig, "")
      .replace(/- .*/g, "")
      .trim();

    const planName = (() => {
      if (cleanedPlanName.includes("enterprise")) {
        return "enterprise";
      }
      if (cleanedPlanName.includes("team")) {
        return "team";
      }
      if (cleanedPlanName.includes("personal")) {
        return "personal";
      }
      if (cleanedPlanName.includes("pro")) {
        return "pro";
      }
      if (cleanedPlanName.includes("starter")) {
        return "starter";
      }
      return "free";
    })();

    const normalizedPlanName = (() => {
      if (cleanedPlanName.includes("enterprise")) {
        return "enterprise";
      }
      if (
        cleanedPlanName.includes("team") ||
        cleanedPlanName.includes("pro")
      ) {
        return "team";
      }
      if (
        cleanedPlanName.includes("personal") ||
        cleanedPlanName.includes("starter")
      ) {
        return "personal";
      }
      return "free";
    })();

    const unlimited = lowerCasePlanName.includes("unlimited");

    const userPermissionLevel = PermissionLevel[normalizedPlanName] || 0;

    const permission = p
      .reduce<{ [K in PermissionName]: boolean }>((acc, feature) => {
        return {
          ...acc,
          [PermissionName[feature.name]]: feature.value <= userPermissionLevel,
        }
      }, {} as { [K in PermissionName]: boolean });

    const quota = p
      .reduce<{ [K in PermissionName]: number }>((acc, feature) => {
        if (!feature.quota) {
          return acc;
        }

        if ("customizedQuota" in feature) {
          return {
            ...acc,
            [PermissionName[feature.name]]: feature.customizedQuota,
          }
        }

        return {
          ...acc,
          [PermissionName[feature.name]]: feature.quota[userPermissionLevel],
        }
      }, {} as { [K in PermissionName]: number });

    const nextPlan = (() => {
      if (maxPermissionLevel > userPermissionLevel) {
        return PermissionLevel[userPermissionLevel + 1];
      }
      return null;
    })() as "personal" | "team" | "enterprise" | null;

    const servicePeriodStartDate = s.subscriptionInfo?.servicePeriodStart
      ? new Date(s.subscriptionInfo.servicePeriodStart)
      : null;
    const servicePeriodEndDate = s.subscriptionInfo?.servicePeriodEnd
      ? new Date(s.subscriptionInfo.servicePeriodEnd)
      : null;


    return {
      plan: {
        name: planName,
        normalized: normalizedPlanName,
        raw: rawPlanName,
        unlimited,
        interval: s.subscriptionInfo?.interval || null,
        level: {
          current: userPermissionLevel,
          max: maxPermissionLevel,
        },
        next: nextPlan,
        seats: s.subscriptionInfo?.seats || null,
        servicePeriodStart: servicePeriodStartDate,
        servicePeriodEnd: servicePeriodEndDate,
      },
      permission,
      quota,
    }
  }, []);

  const getNormalizedApiSubscription = useCallback((params: {
    subscription: GetSubscriptionsType | null;
  }): Pick<UseSubscriptionType, "apiPlan"> => {
    const { subscription: s } = params;

    if (!s ) {
      return { apiPlan: null };
    }

    const rawPlanName = s.plan === "none" ? "Free" : s.plan;
    const lowerCasePlanName = rawPlanName.toLowerCase();
    const cleanedPlanName = lowerCasePlanName
      .replace(/AI Studios API/ig, "")
      .replace(/- .*/g, "")
      .trim();

    const planName = (() => {
      if (cleanedPlanName.includes("enterprise")) {
        return "enterprise";
      }
      if (cleanedPlanName.includes("standard")) {
        return "standard";
      }
      if (cleanedPlanName.includes("premium")) {
        return "premium";
      }
      return "free";
    })();

    const normalizedPlanName = (() => {
      if (cleanedPlanName.includes("enterprise")) {
        return "enterprise";
      }
      if (
        cleanedPlanName.includes("standard")
      ) {
        return "standard";
      }
      if (
        cleanedPlanName.includes("premium")
      ) {
        return "premium";
      }
      return "free";
    })();

    const nextPlan = (() => {
      switch (normalizedPlanName) {
        case "free": {
          return "standard";
        }
        case "standard": {
          return "premium";
        }
        case "premium": {
          return "enterprise";
        }
        default: {
          return null;
        }
      }
    })();

    const userPermissionLevel = (() => {
      switch (normalizedPlanName) {
        case "free": {
          return 0;
        }
        case "standard": {
          return 1;
        }
        case "premium": {
          return 2;
        }
        case "enterprise": {
          return 3;
        }
        default: {
          return 0;
        }
      }
    })()

    const servicePeriodStartDate = s.subscriptionInfo?.servicePeriodStart
      ? new Date(s.subscriptionInfo.servicePeriodStart)
      : null;
    const servicePeriodEndDate = s.subscriptionInfo?.servicePeriodEnd
      ? new Date(s.subscriptionInfo.servicePeriodEnd)
      : null;


    return {
      apiPlan: {
        name: planName,
        normalized: normalizedPlanName,
        raw: rawPlanName,
        level: {
          current: userPermissionLevel,
          max: 3,
        },
        next: nextPlan,
        servicePeriodStart: servicePeriodStartDate,
        servicePeriodEnd: servicePeriodEndDate,
      },
    }
  }, []);

  const getNormalizedCredits = useCallback((params: {
    credits: GetCreditsType | null;
  }): Pick<UseSubscriptionType, "credits"> => {
    const { credits: c } = params;

    if (!c) {
      return { credits: null };
    }
    return {
      credits: {
        credit: c?.credit || 0,
        maxCredit: c?.maxCredit || 0,
        automationCredit: c?.automationCredit || 0,
        apiCredit: c?.apiCredit || 0,
        maxApiCredit: c?.maxApiCredit || 0,
        genCredit: c?.genCredit || {
          basic: 0,
          addOn: 0,
        },
        maxGenCredit: c?.maxGenCredit || {
          basic: 0,
          addOn: 0,
        },
      }
    };
  }, []);

  const getNormalizedGenerativeCredits = useCallback(
    (params: {
      credits: GetCreditsType | null;
      reservedGenerativeCredits: Awaited<
        ReturnType<typeof getReservedGenerativeCredits>
      >["data"] | null;
    }) => {
      const basicCredits = params.credits?.genCredit?.basic ?? 0;
      const addOnCredits = params.credits?.genCredit?.addOn ?? 0;
      const extraCredits =
        params.credits?.genCredit?.extras?.reduce(
          (acc, curr) => acc + curr.credit,
          0,
        ) ?? 0;

      const totalCredits = basicCredits + addOnCredits + extraCredits;

      const reservedCredits = params.reservedGenerativeCredits?.credits ?? 0;

      const remainingCredits = Math.max(totalCredits - reservedCredits, 0);

      return {
        generativeCredits: {
          remainingCredits,
          totalCredits,
          reservedCredits,
          creditDetail: {
            basicCredits,
            addOnCredits,
            extraCredits,
          },

        }
      };
    },
    [],
  );

  const fetch = useCallback(async () => {
    const c = await getCredits().unwrap();
    const s = await getSubscriptions().unwrap();
    const as = await getApiSubscriptions().unwrap();
    const p = await getPermissions().unwrap();
    const rgc = await getReservedGenerativeCredits().unwrap();

    return {
      ...getNormalizedSubscription({ subscription: s, permissions: p }),
      ...getNormalizedApiSubscription({ subscription: as }),
      ...getNormalizedCredits({ credits: c }),
      ...getNormalizedGenerativeCredits({ credits: c, reservedGenerativeCredits: rgc }),
      subscriptionInfo: s?.subscriptionInfo || null,
      apiSubscriptionInfo: as?.subscriptionInfo || null,
    }
  }, [
    getCredits,
    getNormalizedCredits,
    getNormalizedSubscription,
    getNormalizedApiSubscription,
    getNormalizedGenerativeCredits,
    getPermissions,
    getSubscriptions,
    getApiSubscriptions,
    getReservedGenerativeCredits,
  ]);

  const normalizedSubscription = useMemo(() => {
    if (isCreditsLoading || isSubscriptionLoading || isPermissionsLoading) {
      return { plan: null, permission: null, quota: null };
    }

    return getNormalizedSubscription({ subscription, permissions });
  }, [
    getNormalizedSubscription,
    isCreditsLoading,
    isPermissionsLoading,
    isSubscriptionLoading,
    permissions,
    subscription,
  ]);

  const normalizedApiSubscription = useMemo(() => {
    if (isApiSubscriptionLoading) {
      return { apiPlan: null };
    }
    return getNormalizedApiSubscription({ subscription: apiSubscription });
  }, [getNormalizedApiSubscription, isApiSubscriptionLoading, apiSubscription]);

  const normalizedCredits = useMemo(() => {
    if (isCreditsLoading || isSubscriptionLoading || isPermissionsLoading) {
      return { credits: null };
    }

    return getNormalizedCredits({ credits });
  }, [
    getNormalizedCredits,
    isCreditsLoading,
    isPermissionsLoading,
    isSubscriptionLoading,
    credits,
  ]);

  const normalizedGenerativeCredits = useMemo(() => {
    if (
      isCreditsLoading ||
      isSubscriptionLoading ||
      isPermissionsLoading ||
      isReservedGenerativeCreditsLoading
    ) {
      return { generativeCredits: null };
    }
    return getNormalizedGenerativeCredits({
      credits,
      reservedGenerativeCredits,
    });
  }, [
    getNormalizedGenerativeCredits,
    isCreditsLoading,
    isSubscriptionLoading,
    isPermissionsLoading,
    isReservedGenerativeCreditsLoading,
    credits,
    reservedGenerativeCredits,
  ]);

  const onFetch = useEffectEvent(fetch);

  return {
    isLoading,
    isFetching,
    refetch,
    fetch: onFetch,
    ...normalizedSubscription,
    ...normalizedApiSubscription,
    ...normalizedCredits,
    ...normalizedGenerativeCredits,
    subscriptionInfo: subscription?.subscriptionInfo || null,
    apiSubscriptionInfo: apiSubscription?.subscriptionInfo || null,
  }
}